/**
 * Typed HTTP client for the sales-agent admin backend.
 *
 * - Dev: base URL = "/api" → proxied by Vite to http://api:3000
 * - Prod: base URL = "/api" → same origin, served by Fastify
 *
 * Story 7.3 rewrite: uses axios with a full auth interceptor stack:
 *   - request: injects `Authorization: Bearer {access_token}` from localStorage
 *   - response: on 401, attempts `/admin/auth/refresh` (once, deduplicated),
 *     retries the original request; on failure clears storage and redirects.
 *   - progress: a tiny in-module request counter drives the global top bar.
 *
 * Auth token lifecycle lives in `stores/auth.ts`; this module is intentionally
 * transport-only and avoids reaching into Zustand to keep the axios module
 * tree-shakable and unit-testable in isolation.
 */

import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const USER_KEY = 'admin_user';

const BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/+$/, '');

/** Subscriber callback for the inflight-request counter. */
type ProgressListener = (pending: number) => void;

const progressListeners = new Set<ProgressListener>();
let pendingCount = 0;

function bumpPending(delta: number): void {
  pendingCount = Math.max(0, pendingCount + delta);
  for (const listener of progressListeners) {
    listener(pendingCount);
  }
}

/**
 * Subscribe to inflight request count changes. Returns an unsubscribe fn.
 * Used by `GlobalProgressBar` to animate a thin top bar during requests.
 */
export function onRequestProgress(listener: ProgressListener): () => void {
  progressListeners.add(listener);
  listener(pendingCount);
  return () => {
    progressListeners.delete(listener);
  };
}

/** Read-only access to the inflight counter (testing). */
export function getPendingRequestCount(): number {
  return pendingCount;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

/**
 * Callback the auth store registers so interceptors can push rotated tokens
 * back into Zustand + localStorage in one place. We avoid a direct import to
 * prevent circular dependencies.
 */
type TokenUpdateCallback = (tokens: AuthTokens) => void;
type LogoutCallback = () => void;

let onTokenUpdate: TokenUpdateCallback = (tokens) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
};

let onLogout: LogoutCallback = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

export function registerAuthCallbacks(callbacks: {
  onTokenUpdate: TokenUpdateCallback;
  onLogout: LogoutCallback;
}): void {
  onTokenUpdate = callbacks.onTokenUpdate;
  onLogout = callbacks.onLogout;
}

export const api: AxiosInstance = axios.create({
  baseURL: BASE,
  headers: { Accept: 'application/json' },
});

// ---------------------------------------------------------------------------
// Request interceptor — attach bearer token + bump progress counter
// ---------------------------------------------------------------------------

api.interceptors.request.use((config) => {
  const token = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  bumpPending(1);
  return config;
});

// ---------------------------------------------------------------------------
// Response interceptor — 401 refresh flow with deduplicated in-flight refresh
// ---------------------------------------------------------------------------

interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let refreshing: Promise<string> | null = null;

async function doRefresh(): Promise<string> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    throw new Error('no_refresh_token');
  }
  // Use a bare axios call so we don't recurse through our own interceptors —
  // a 401 on /refresh would otherwise loop forever.
  const { data } = await axios.post<RefreshResponse>(
    `${BASE}/admin/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );
  onTokenUpdate({ accessToken: data.accessToken, refreshToken: data.refreshToken });
  return data.accessToken;
}

api.interceptors.response.use(
  (response) => {
    bumpPending(-1);
    return response;
  },
  async (error: AxiosError) => {
    bumpPending(-1);
    const original = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    if (!original || status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    // Skip refresh for endpoints that produce/validate credentials (a 401 there
    // means bad creds, not an expired access token) and when there's no refresh
    // token stored at all (fresh visit → nothing to refresh, let the original
    // error propagate so callers can show a proper message).
    if (
      original.url?.includes('/admin/auth/refresh') ||
      original.url?.includes('/admin/auth/login') ||
      !localStorage.getItem(REFRESH_TOKEN_KEY)
    ) {
      return Promise.reject(error);
    }

    original._retry = true;
    refreshing ??= doRefresh();

    try {
      const newAccess = await refreshing;
      original.headers.set('Authorization', `Bearer ${newAccess}`);
      return api(original);
    } catch (refreshError) {
      onLogout();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      refreshing = null;
    }
  },
);

// ---------------------------------------------------------------------------
// Convenience wrapper (mirrors the old fetch-based api shape for callers)
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;

  constructor(status: number, body: unknown) {
    super(`API ${status}: ${typeof body === 'string' ? body : JSON.stringify(body)}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function toApiError<T>(promise: Promise<{ data: T }>): Promise<T> {
  try {
    const { data } = await promise;
    return data;
  } catch (err) {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status ?? 0;
      throw new ApiError(status, err.response?.data ?? err.message);
    }
    throw err;
  }
}

export const apiClient = {
  get: <T>(path: string): Promise<T> => toApiError(api.get<T>(path)),
  post: <T>(path: string, body?: unknown): Promise<T> => toApiError(api.post<T>(path, body ?? {})),
  put: <T>(path: string, body?: unknown): Promise<T> => toApiError(api.put<T>(path, body ?? {})),
  patch: <T>(path: string, body?: unknown): Promise<T> =>
    toApiError(api.patch<T>(path, body ?? {})),
  delete: <T>(path: string): Promise<T> => toApiError(api.delete<T>(path)),
};

export type ApiClient = typeof apiClient;
